import type React from "react"
import axios from "axios"
import moment from "moment"
import { toast } from "react-toastify"

import { BASE_URL } from "../../../global"
import type { ActivityLogicCommon, PlanLessonActivity } from "./PlanLessonTypes"

interface TopicActivityLike {
  activity_node_id: number | string
  node_name?: string
  question_id?: number
  [key: string]: any
}

interface LevelTopicLike {
  subject_id: string | number
  activity_topic_id?: number | string
  activity_level_id?: number | string
  level_name?: string
  topic_name?: string
  TopicActivities: TopicActivityLike[]
}

interface AssignActivityLevelItem {
  activity_node_id: number | string
  LevelTopics: LevelTopicLike[]
  [key: string]: any
}

interface AssignActivityLevelDataLike {
  activities?: AssignActivityLevelItem[]
}

interface FetchAndProcessDataParams {
  assignActivityLevelData: AssignActivityLevelDataLike | null | undefined
  IsNextActivity: boolean
  homeworkActivityArray: PlanLessonActivity[]
  lessonActivityArray: PlanLessonActivity[]
  CurrentActivityData: PlanLessonActivity | null
  activityIsHw: number
  setExistingValue: React.Dispatch<React.SetStateAction<any[]>>
  setAssignActivityLevelData: (value: any) => void
  setNextActivity: (value: boolean) => void
  handleLocalStorageAssignClick: (
    topicTitle: any,
    topic: any,
    activity: any,
    acttype: number
  ) => Promise<unknown>
  OnSubmitActivityData: () => void | Promise<void>
}

interface FetchNextAssessmentParams extends ActivityLogicCommon {
  subjectId: string | number
  pk_lesson_activity_key: string | number
  marked_for_homework: string | number
  is_homeworkAssing: number
  setAssignActivityLevelData: (value: any) => void
  setInnerLoder: (value: boolean) => void
  setActivityIsHW: (value: number) => void
  navigate: (path: string) => void
}

interface FetchNextDiagnosticsParams extends ActivityLogicCommon {
  activity: PlanLessonActivity
  setAssignDiagnosticsLevelData: (value: any) => void
  setCurrentLevelIndex: (value: number) => void
  setInnerLoder: (value: boolean) => void
  setCurrentDiagnosticslistActivity: (value: any) => void
  setActivityIsHW: (value: number) => void
  navigate: (path: string) => void
}

interface FetchSelectedAssessParams extends ActivityLogicCommon {
  subId: string | number
  nodeId: string | number
  setInnerLoder: (value: boolean) => void
  setSubjectIdData: (value: string | number) => void
}

interface FetchSelectedDiagnosParams extends ActivityLogicCommon {
  subId: string | number
  nodeId: string | number
}

interface FetchSelectActivityParams {
  authUser: string | null
  activity_node_id: string | number
  subjectIdData: string | number
  setInnerLoder: (value: boolean) => void
  navigate: (path: string) => void
}

interface HandleLocalStorageAssignClickParams extends ActivityLogicCommon {
  topicTitle: any
  topic: any
  activity: any
  acttype: number
  existingData: any[]
  subjectIdData: string | number
  setInnerLoder: (value: boolean) => void
  setSubjectIdData: (value: string | number) => void
  setExistingValue: React.Dispatch<React.SetStateAction<any[]>>
  navigate: (path: string) => void
}

interface HandleCommentChangeParams {
  id: string | number
  value: string
  setExistingValue: React.Dispatch<React.SetStateAction<any[]>>
}

interface SubmitActivityDataParams extends ActivityLogicCommon {
  activityIsHw: number
  setExistingValue: React.Dispatch<React.SetStateAction<any[]>>
  fetchLessonData: () => void | Promise<void>
  fetchHomeWorkData: () => void | Promise<void>
  setIsSubmitting: (value: boolean) => void
}

interface RemoveLocalStorageAssignItemParams {
  id: string | number
  existingData: any[]
  setExistingValue: React.Dispatch<React.SetStateAction<any[]>>
}

const DURATION_LABEL_TO_MINUTES: Record<string, number> = {
  "Less than 2 minutes": 2,
  "2-5 minutes": 5,
  "5-10 minutes": 10,
  "10-15 minutes": 15,
  "15-20 minutes": 20,
  "20-30 minutes": 30,
  "30-60 minutes": 60,
}

function setAuthorizationHeader(authUser: string): void {
  axios.defaults.headers.common["Authorization"] = `Bearer ${authUser}`
}

function readSelectedData(): any[] {
  return JSON.parse(localStorage.getItem("selectedData") || "[]")
}

function writeSelectedData(value: any[]): void {
  localStorage.setItem("selectedData", JSON.stringify(value))
}

function clearSelectedData(
  setExistingValue?: React.Dispatch<React.SetStateAction<any[]>>
): void {
  localStorage.removeItem("selectedData")
  setExistingValue?.([])
}

function convertDurationLabelToMinutes(
  label: string | null | undefined
): number {
  return DURATION_LABEL_TO_MINUTES[label || ""] || 0
}

function findTopicForActivity(
  activitiesData: AssignActivityLevelItem,
  activityNodeId: number | string
): LevelTopicLike | undefined {
  return activitiesData.LevelTopics.find((topic: LevelTopicLike) =>
    topic.TopicActivities.some(
      (activity: TopicActivityLike) =>
        activity.activity_node_id == activityNodeId
    )
  )
}

function findNextTopicActivity(
  topicActivities: TopicActivityLike[],
  activityNodeId: number | string
): TopicActivityLike | null {
  const currentIndex = topicActivities.findIndex(
    activity => activity.activity_node_id == activityNodeId
  )

  if (currentIndex < 0 || currentIndex >= topicActivities.length - 1) {
    return null
  }

  return topicActivities[currentIndex + 1]
}

function isActivityInArray(
  activities: PlanLessonActivity[],
  activityNodeId: number | string
): boolean {
  return activities.some(
    activity => activity.fk_activity_node_id == activityNodeId
  )
}

function getAssessmentCoinTotal(
  selectedActivity: any,
  subjectId: string | number
): number {
  let totalCoins = 0
  const numericSubjectId = Number(subjectId)

  if (numericSubjectId === 1) {
    ;(selectedActivity?.english_question_activities || []).forEach(
      (activityItem: any) => {
        const question = activityItem?.english_question

        if (
          question &&
          [10, 13].includes(
            parseInt(String(question?.english_question_category_id || 0), 10)
          )
        ) {
          const parts = question?.english_question_parts || []
          const answerableParts = parts.filter(
            (part: any) =>
              part.english_question_answers?.length > 0 &&
              part.english_question_answers[0]?.english_answer_text &&
              part.english_question_answers[0]?.english_answer_text !== ""
          )
          totalCoins += answerableParts.length * 2
        } else if (question) {
          totalCoins += (question?.english_question_parts || []).length * 2
        }
      }
    )
  } else if ([2, 3].includes(numericSubjectId)) {
    ;(selectedActivity?.question_activities || []).forEach(
      (activityItem: any) => {
        totalCoins += (activityItem?.question?.question_parts || []).length * 2
      }
    )
  }

  return selectedActivity && totalCoins >= 0
    ? totalCoins
    : selectedActivity?.totalScore ?? 2
}

function getAssessmentDurationMinutes(
  selectedActivity: any,
  subjectId: string | number
): number {
  let totalMinutes = 0
  const numericSubjectId = Number(subjectId)

  if (numericSubjectId === 1) {
    ;(selectedActivity?.english_question_activities || []).forEach(
      (activityItem: any) => {
        if (activityItem?.english_question) {
          totalMinutes += convertDurationLabelToMinutes(
            activityItem.english_question.english_question_time_needed ||
              "Less than 2 minutes"
          )
        }
      }
    )
  } else if (numericSubjectId === 2) {
    ;(selectedActivity?.question_activities || []).forEach(
      (activityItem: any) => {
        if (activityItem?.question) {
          totalMinutes += convertDurationLabelToMinutes(
            activityItem.question.question_time_needed || "Less than 2 minutes"
          )
        }
      }
    )
  }

  return totalMinutes > 0 ? totalMinutes : 10
}

function getDiagnosticsNodeIds(
  topicTitle: any,
  subjectId: string | number
): number[] {
  const numericSubjectId = Number(subjectId)

  const topicNodes = (
    topicTitle?.activity_master_topic?.activity_level_has_topic_nodes || []
  )
    .map((topicData: any) => {
      if (numericSubjectId === 1) {
        if (topicData?.activity_node?.english_diagnostic_question_activities) {
          return topicData.activity_node.english_diagnostic_question_activities.map(
            (node: any) => Number(node.question_id)
          )
        }
        return []
      }

      if (numericSubjectId === 2) {
        if (topicData?.activity_node?.diagnostic_question_activities) {
          return topicData.activity_node.diagnostic_question_activities.map(
            (node: any) => Number(node.question_id)
          )
        }
        return []
      }

      return []
    })
    .flat()
    .filter((nodeId: number) => !Number.isNaN(nodeId))

  return [...new Set<number>(topicNodes)].filter(nodeId => nodeId !== 0)
}

function getDiagnosticsDurationMinutes(
  selectedActivity: any,
  subjectId: string | number
): number {
  let totalMinutes = 0
  const numericSubjectId = Number(subjectId)

  if (numericSubjectId === 1) {
    ;(selectedActivity?.questionData || []).forEach((activityItem: any) => {
      if (activityItem?.english_diagnostics) {
        totalMinutes += convertDurationLabelToMinutes(
          activityItem.english_diagnostics.diagnostic_time_needed ||
            "Less than 2 minutes"
        )
      }
    })
  } else if (numericSubjectId === 2) {
    ;(selectedActivity?.questionData || []).forEach((activityItem: any) => {
      if (activityItem?.diagnostics) {
        totalMinutes += convertDurationLabelToMinutes(
          activityItem.diagnostics.diagnostic_time_needed ||
            "Less than 2 minutes"
        )
      }
    })
  }

  return totalMinutes > 0 ? totalMinutes : 10
}

function normalizeDiagnosticsPreviewData(responseData: any): any {
  if (responseData?.questionData) {
    return responseData
  }

  if (responseData?.data?.questionData) {
    return responseData.data
  }

  if (Array.isArray(responseData?.data)) {
    return { questionData: responseData.data }
  }

  if (Array.isArray(responseData)) {
    return { questionData: responseData }
  }

  return responseData || { questionData: [] }
}

function createEmptyMergedData(): Record<string, any> {
  return {
    id: "",
    name: "",
    subject_id: 0,
    topicID: 0,
    coin: 0,
    duration: "",
    is_homework: 0,
    type: "",
    activity_node_id: "",
    activity_type: "",
    tutor_comments: "",
    level_id: "",
    level_name: "",
    topic_name: "",
  }
}

function createAssessmentMergedData(params: {
  topicTitle: any
  topic: any
  activity: any
  selectedActivity: any
}): Record<string, any> {
  const { topicTitle, topic, activity, selectedActivity } = params

  return {
    id: topicTitle.activity_node_id,
    name: topicTitle.node_name,
    subject_id: topic.subject_id,
    topicID: topic.activity_topic_id,
    coin: getAssessmentCoinTotal(selectedActivity, topic.subject_id),
    duration: `${getAssessmentDurationMinutes(
      selectedActivity,
      topic.subject_id
    )} mins`,
    is_homework: 0,
    type: "Online Activities",
    tutor_comments: "",
    activity_node_id: topicTitle.activity_node_id,
    activity_type: 0,
    level_id: activity.level_id,
    level_name: activity.level_name,
    topic_name: topic.topic_name,
  }
}

function createDiagnosticsMergedData(params: {
  topicTitle: any
  topic: any
  selectedActivity: any
  acttype: number
}): Record<string, any> {
  const { topicTitle, topic, selectedActivity, acttype } = params
  const uniqueTopicNodes = getDiagnosticsNodeIds(topicTitle, topic.subject_id)

  return {
    id: uniqueTopicNodes,
    name: topicTitle.activity_master_topic.topic_name,
    subject_id: topic.subject_id,
    topicID: topicTitle.activity_master_topic.activity_topic_id,
    coin:
      selectedActivity?.questionData?.length > 0
        ? selectedActivity.questionData.length * 2
        : 2,
    duration: `${getDiagnosticsDurationMinutes(
      selectedActivity,
      topic.subject_id
    )} mins`,
    is_homework: 0,
    type: acttype == 2 ? "Diagnostics" : "",
    activity_node_id: uniqueTopicNodes,
    activity_type: 2,
    level_id: topic.activity_level_id,
    level_name: topic.level_name,
    topic_name: "",
  }
}

export async function fetchAndProcessDataLogic({
  assignActivityLevelData,
  IsNextActivity,
  homeworkActivityArray,
  lessonActivityArray,
  CurrentActivityData,
  activityIsHw,
  setExistingValue,
  setAssignActivityLevelData,
  setNextActivity,
  handleLocalStorageAssignClick,
  OnSubmitActivityData,
}: FetchAndProcessDataParams): Promise<void> {
  if (!assignActivityLevelData?.activities || !IsNextActivity) return

  try {
    clearSelectedData(setExistingValue)
    console.log("Existing data removed")

    const activitiesData = assignActivityLevelData.activities[0]
    const activityNodeId = activitiesData.activity_node_id
    const topicData = findTopicForActivity(activitiesData, activityNodeId)

    if (!topicData) {
      toast.error("Sorry, topic data not found", { autoClose: 3000 })
      return
    }

    const nextActivity = findNextTopicActivity(
      topicData.TopicActivities || [],
      activityNodeId
    )

    if (!nextActivity) {
      setAssignActivityLevelData([])
      setNextActivity(false)
      toast.error("No next activity available", { autoClose: 700 })
      return
    }

    // Check against the TARGET list (where the next activity is being added),
    // not the source. activityIsHw === 1 means adding to homework,
    // activityIsHw === 0 means adding to lesson plan.
    const isExistingLesson =
      Number(activityIsHw) === 1
        ? isActivityInArray(
            homeworkActivityArray,
            nextActivity.activity_node_id
          )
        : isActivityInArray(lessonActivityArray, nextActivity.activity_node_id)

    const existingData = readSelectedData()
    const isExisting = existingData.some(
      (data: any) => data.activity_node_id == nextActivity.activity_node_id
    )

    if (isExisting || isExistingLesson) {
      toast.error("This activity is already assigned", {
        autoClose: 700,
      })
      return
    }

    await handleLocalStorageAssignClick(
      nextActivity,
      topicData,
      activitiesData,
      0
    )
    await OnSubmitActivityData()
    setAssignActivityLevelData([])
    setNextActivity(false)
    setExistingValue([])
  } catch (error) {
    console.error("Error processing activity data:", error)
    toast.error("An error occurred while processing data", {
      autoClose: 3000,
    })
  }
}

export async function fetchNextAssessmentActivityDataLogic({
  authUser,
  session_key,
  student_key,
  subjectId,
  pk_lesson_activity_key,
  marked_for_homework,
  is_homeworkAssing,
  setAssignActivityLevelData,
  setInnerLoder,
  setActivityIsHW,
  navigate,
}: FetchNextAssessmentParams): Promise<void> {
  setAssignActivityLevelData([])

  if (!authUser) {
    navigate("/login")
    return
  }

  if (!session_key || !student_key) {
    return
  }

  setInnerLoder(true)

  try {
    setAuthorizationHeader(authUser)

    const response = await axios.post(
      BASE_URL + "activity/getAssessmentActivitiesData",
      {
        is_homework: marked_for_homework,
        session_key,
        student_key,
        subject_id: subjectId,
        pk_lesson_activity_key,
      }
    )

    setAssignActivityLevelData(response.data)
    setActivityIsHW(is_homeworkAssing)
  } catch (error) {
    console.error("Error fetching options from API: ", error)
  } finally {
    setInnerLoder(false)
  }
}

export async function fetchNextDiagnosticsActivityDataLogic({
  authUser,
  session_key,
  student_key,
  activity,
  setAssignDiagnosticsLevelData,
  setCurrentLevelIndex,
  setInnerLoder,
  setCurrentDiagnosticslistActivity,
  setActivityIsHW,
  navigate,
}: FetchNextDiagnosticsParams): Promise<void> {
  setAssignDiagnosticsLevelData([])
  setCurrentLevelIndex(0)

  if (!authUser) {
    navigate("/login")
    return
  }

  if (!session_key || !student_key) {
    return
  }

  setInnerLoder(true)

  try {
    setAuthorizationHeader(authUser)

    const response = await axios.post(
      BASE_URL + "activity/getDiagnosticsActivities",
      {
        is_homework: activity.marked_for_homework,
        session_key,
        student_key,
        subject_id: activity.fk_sub_id,
      }
    )

    setAssignDiagnosticsLevelData([response.data])
    setCurrentDiagnosticslistActivity(activity)
    setActivityIsHW(Number(activity.marked_for_homework))
  } catch (error) {
    console.error("Error fetching options from API: ", error)
  } finally {
    setInnerLoder(false)
  }
}

export function activityAttemptedStatusLogic(AttemptData: {
  lastTwoWeekExistDate: string
}): void {
  toast.info(
    "This activity is already assigned, and the date is " +
      moment(AttemptData.lastTwoWeekExistDate, moment.ISO_8601).format(
        "DD MMM YYYY (ddd)"
      ) +
      ".",
    {
      autoClose: 3000,
    }
  )
}

export async function fetchSelectedAssessDataLogic({
  authUser,
  subId,
  nodeId,
  session_key,
  student_key,
  setInnerLoder,
  setSubjectIdData,
}: FetchSelectedAssessParams): Promise<any[]> {
  if (!authUser) {
    return []
  }

  setInnerLoder(true)
  setSubjectIdData(subId)

  try {
    setAuthorizationHeader(authUser)

    const response = await axios.post(
      BASE_URL + "activity/previewAssessmentDetails",
      {
        activity_node_id: nodeId,
        subject_id: subId,
        session_key,
        student_key,
      }
    )

    return response.data.data || []
  } catch (error) {
    console.error("Error in fetching List Library deatails:", error)
    return []
  } finally {
    setInnerLoder(false)
  }
}

export async function fetchSelectedDiagnosDataLogic({
  authUser,
  subId,
  nodeId,
  session_key,
  student_key,
}: FetchSelectedDiagnosParams): Promise<any> {
  if (!authUser) {
    return { questionData: [] }
  }

  try {
    setAuthorizationHeader(authUser)

    const response = await axios.post(
      BASE_URL + "activity/previewDiagnosticsDetails",
      {
        activity_node_id: nodeId,
        subject_id: subId,
        session_key,
        student_key,
      }
    )

    return normalizeDiagnosticsPreviewData(response.data)
  } catch (error) {
    console.error("Error in fetching List Library deatails:", error)
    return { questionData: [] }
  }
}

export async function fetchSelectActivityDataLogic({
  authUser,
  activity_node_id,
  subjectIdData,
  setInnerLoder,
  navigate,
}: FetchSelectActivityParams): Promise<void> {
  if (!authUser) {
    navigate("/login")
    return
  }

  setInnerLoder(true)

  try {
    setAuthorizationHeader(authUser)

    await axios.post(BASE_URL + "activity/selectActivity", {
      activity_node_id,
      subject_id: subjectIdData,
    })
  } catch (error) {
    console.error("Error fetching options from API: ", error)
  } finally {
    setInnerLoder(false)
  }
}

export async function handleLocalStorageAssignClickLogic({
  topicTitle,
  topic,
  activity,
  acttype,
  existingData,
  authUser,
  session_key,
  student_key,
  subjectIdData,
  setInnerLoder,
  setSubjectIdData,
  setExistingValue,
  navigate,
}: HandleLocalStorageAssignClickParams): Promise<void> {
  let selectedActivity: any = []

  if (acttype == 0) {
    selectedActivity = await fetchSelectedAssessDataLogic({
      authUser,
      subId: topic.subject_id,
      nodeId: topicTitle.activity_node_id,
      session_key,
      student_key,
      setInnerLoder,
      setSubjectIdData,
    })
  } else if (acttype == 2) {
    selectedActivity = await fetchSelectedDiagnosDataLogic({
      authUser,
      subId: topic.subject_id,
      nodeId: topicTitle.activity_master_topic.activity_topic_id,
      session_key,
      student_key,
    })
  }

  let mergedData: Record<string, any> = createEmptyMergedData()

  if (acttype == 0) {
    void fetchSelectActivityDataLogic({
      authUser,
      activity_node_id: topicTitle.activity_node_id,
      subjectIdData: topic.subject_id ?? subjectIdData,
      setInnerLoder,
      navigate,
    })

    mergedData = createAssessmentMergedData({
      topicTitle,
      topic,
      activity,
      selectedActivity,
    })
  } else if (acttype == 2) {
    mergedData = createDiagnosticsMergedData({
      topicTitle,
      topic,
      selectedActivity,
      acttype,
    })
  }

  const newValue = existingData.concat(mergedData)
  writeSelectedData(newValue)
  setExistingValue(newValue)
}

export function handleCommentChangeLogic({
  id,
  value,
  setExistingValue,
}: HandleCommentChangeParams): void {
  setExistingValue(prevData => {
    const updatedData = prevData.map(item =>
      item.id === id ? { ...item, tutor_comments: value } : item
    )

    writeSelectedData(updatedData)
    return updatedData
  })
}

export async function submitActivityDataLogic({
  authUser,
  session_key,
  student_key,
  activityIsHw,
  setExistingValue,
  fetchLessonData,
  fetchHomeWorkData,
  setIsSubmitting,
}: SubmitActivityDataParams): Promise<void> {
  try {
    setExistingValue([])

    if (!authUser || !session_key || !student_key) {
      return
    }

    setAuthorizationHeader(authUser)

    await axios.post(BASE_URL + "activity/createActivities", {
      activities: readSelectedData(),
      is_homework: activityIsHw || 0,
      lesson_key:
        localStorage.getItem("lesson_key") &&
        localStorage.getItem("lesson_key") != null
          ? localStorage.getItem("lesson_key")
          : "",
      session_key: session_key || "",
      student_key: student_key || "",
    })

    toast.success(" Activities added successfully", {
      autoClose: 700,
    })

    setExistingValue([])
    clearSelectedData()
    await fetchLessonData()
    await fetchHomeWorkData()
  } catch (error) {
    console.error("Error fetching data:", error)
    toast.error("Failure! Unable to create notes. Try again later", {
      autoClose: 3000,
    })
  } finally {
    setIsSubmitting(false)
  }
}

export function removeLocalStorageAssignItemLogic({
  id,
  existingData,
  setExistingValue,
}: RemoveLocalStorageAssignItemParams): void {
  const updatedItems = existingData.filter(item => item.id !== id)
  writeSelectedData(updatedItems)
  setExistingValue(updatedItems)
}
